/**
 * /add-watermark — one screen: your page on the left, the settings on the
 * right, and no step between changing something and seeing it.
 *
 * WHAT THIS REPLACED. Roughly 250 lines of inline blade script driving four
 * stacked panels and a CSS mock-up of "a document". Three things were wrong
 * with it beyond the layout:
 *
 *   1. The preview was not the file. It could not show tiling, ignored the
 *      margins, and knew nothing about the PDF that had just been uploaded.
 *   2. The rotation slider was dead in two of the eleven positions: choosing
 *      "diagonal" or "tile" made the worker force -45 degrees and ignore it.
 *      A control that silently does nothing is worse than a missing one.
 *   3. Grid positions were computed from the UNROTATED text width, so a
 *      rotated corner stamp hung off the page — invisible until download.
 *
 * The fix for all three is structural: placement moved into layout.js, which
 * both this page and the worker read, and "diagonal"/"tile" stopped being
 * positions. Diagonal is now what it always was — a rotation — and tiling is a
 * repeat toggle that composes with every other setting.
 *
 * Loads pdf.js once and uses it for both jobs it is needed for (page count and
 * preview); the old page loaded it to count pages and then drew a fake one.
 */
(function () {
    'use strict';

    var CFG = window.__WM_CFG || {};
    var T = CFG.i18n || {};
    var SLUG = 'add-watermark';

    function $(id) { return document.getElementById(id); }
    function t(k, f) { return T[k] || f || k; }
    function toast(m, kind) { if (window.__toast) window.__toast(m, kind || 'info'); }

    var _funnel = {};
    function funnel(stage, p) {
        if (_funnel[stage]) return;
        _funnel[stage] = 1;
        try { if (window.__aeFunnel) window.__aeFunnel(stage, p || {}); } catch (e) {}
    }
    function stage(name, p) {
        try { if (window.__stage) window.__stage(name, p || {}); } catch (e) {}
    }
    function ae(f, p) {
        try { if (window.__ae) window.__ae('feature_use', Object.assign({ slug: SLUG, f: f }, p || {})); } catch (e) {}
    }

    // -- state -----------------------------------------------------------

    var S = {
        mode: 'text',
        text: '',
        font: 'helvetica-bold',
        size: 80,
        color: '#999999',
        image: null,            // ImageBitmap / HTMLImageElement for the preview
        imageFile: null,
        imageWidthPct: 30,
        position: 'middle-center',
        freeX: 0.5, freeY: 0.5,
        rotation: 0,
        opacity: 30,
        tile: false,
        tileDensity: 3,
        layer: 'over',
        pageRange: '',
        outputName: '',
        ready: false,
        sizeTouched: false,
    };

    var source = null;          // the ACTIVE file: { file, bytes, name, pageCount }
    /* The queue. Only the active file's bytes and pdf.js document are held —
       the rest are Files, whose bytes the OS still owns. Reading ten PDFs
       up front to show a preview of one is how a batch tool runs a laptop out
       of memory before it has done any work. */
    var queue = [];             // File[]
    var activeIdx = 0;
    var pdfjs = null;
    var pdfDoc = null;
    var preview = null;
    var worker = null;
    var exportState = 'idle';
    var exportBlob = null, exportName = '';
    var limitTicket = null;
    var delivered = false;
    var pending = null;         // the stampOne() promise the worker is answering
    var substitutionTold = false;

    // -- pdf.js ----------------------------------------------------------

    async function loadPdfJs() {
        if (pdfjs) return pdfjs;
        var mod = await import(CFG.pdfjsUrl);
        mod.GlobalWorkerOptions.workerSrc = CFG.pdfjsWorkerUrl;
        pdfjs = mod;
        return mod;
    }

    // -- helpers ---------------------------------------------------------

    function humanSize(b) {
        if (!b) return '0 KB';
        if (b < 1024) return b + ' B';
        if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
        return (b / 1024 / 1024).toFixed(1) + ' MB';
    }
    function baseName(s) { return String(s || 'output').replace(/\.[^.]+$/, ''); }
    function pluralPages(n) {
        return n === 1
            ? t('file_pages_one', '1 page').replace(':count', '1')
            : t('file_pages', ':count pages').replace(':count', String(n));
    }

    function stampText() {
        return S.mode === 'text' ? (S.text || '') : '';
    }

    // -- preview glue ----------------------------------------------------

    function previewState() {
        return {
            mode: S.mode,
            text: S.text || ($('wmText') && $('wmText').placeholder) || '',
            font: S.font, size: S.size, color: S.color,
            image: S.image, imageWidthPct: S.imageWidthPct,
            position: S.position, freeX: S.freeX, freeY: S.freeY,
            rotation: S.rotation, opacity: S.opacity,
            tile: S.tile, tileDensity: S.tileDensity,
            layer: S.layer,
            ready: S.ready,
        };
    }

    function repaint() { if (preview) preview.repaint(); }

    /** Any change to what the output would be invalidates a finished export. */
    function touched() {
        if (exportState === 'done' || exportState === 'err') {
            exportBlob = null;
            setExportState('idle');
        }
        repaint();
        syncEnabled();
    }

    // -- placement UI ----------------------------------------------------

    function markGrid() {
        var btns = document.querySelectorAll('.wm-cell');
        for (var i = 0; i < btns.length; i++) {
            var on = !S.tile && btns[i].dataset.pos === S.position;
            btns[i].classList.toggle('is-on', on);
            btns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
        }
        var pad = $('wmGrid');
        if (pad) pad.classList.toggle('is-off', S.tile);
        var free = $('wmFreeNote');
        if (free) free.hidden = S.tile || S.position !== 'free';
    }

    function setPosition(pos) {
        S.position = pos;
        markGrid();
        touched();
    }

    // -- size ------------------------------------------------------------

    /**
     * The size at which the stamp spans the page, at the current angle.
     *
     * Closed form rather than a search: the box is size * (u*|cos| + 0.72*|sin|)
     * where u is the text width at size 1, so the size that fits a target width
     * is one division. 0.94 keeps a hair of margin, because the canvas metric
     * used here and pdf-lib's own differ by a per cent or two.
     */
    function fitSize() {
        if (!preview) return null;
        var box = preview.pageSize();
        var target = (box.w - 72) * 0.94;
        if (S.mode === 'image') return null;
        var text = S.text || '';
        if (!text) return null;
        var c = document.createElement('canvas').getContext('2d');
        c.font = window.WmPreview._fontCss(S.font, 100);
        var u = c.measureText(text).width / 100;
        var r = Math.abs(S.rotation) * Math.PI / 180;
        var denom = u * Math.abs(Math.cos(r)) + 0.72 * Math.abs(Math.sin(r));
        if (!(denom > 0)) return null;
        return Math.max(6, Math.min(400, Math.round(target / denom)));
    }

    /**
     * Keep the stamp inside the page until the visitor says otherwise.
     *
     * The default size of 80 pt is right for "COPY" and wrong for
     * «КОНФИДЕНЦИАЛЬНО», which at 80 pt and -45 degrees runs off both edges of
     * an A4 page. The old tool shipped that and nobody could see it, because
     * the preview was a mock-up; with a real preview it is the first thing you
     * notice. So the size follows the text until the size field is edited, and
     * after that it is the visitor's number and stays put.
     */
    function autoFit() {
        if (S.sizeTouched || S.mode !== 'text') return;
        var v = fitSize();
        if (!v) return;
        // Only ever shrink to fit: growing a deliberately small mark to page
        // width would be a second surprise.
        v = Math.min(v, 80);
        if (v === S.size) return;
        S.size = v;
        var el = $('wmSize');
        if (el) el.value = v;
    }

    // -- export state ----------------------------------------------------

    function syncEnabled() {
        var btn = $('wmGo');
        if (!btn) return;
        var ok = !!(queue.length && source && source.pageCount)
            && (S.mode === 'text' ? !!(S.text || '').trim() : !!S.imageFile);
        if (exportState === 'idle') btn.disabled = !ok;
    }

    function setExportState(next) {
        exportState = next;
        var btn = $('wmGo');
        var label = btn && btn.querySelector('.wm-go-label');
        var prog = $('wmProg'), bar = $('wmProgBar'), txt = $('wmProgText');
        if (!btn || !label) return;
        btn.classList.remove('is-busy', 'is-done', 'is-err');
        switch (next) {
            case 'busy':
                btn.classList.add('is-busy'); btn.disabled = false;
                label.textContent = t('cancel', 'Cancel');
                if (prog) prog.classList.add('is-on');
                break;
            case 'done':
                btn.classList.add('is-done'); btn.disabled = false;
                label.textContent = t('saved', 'Saved');
                if (prog) prog.classList.remove('is-on');
                if (bar) bar.style.width = '0%';
                if (txt) txt.textContent = '';
                break;
            case 'err':
                btn.classList.add('is-err'); btn.disabled = false;
                label.textContent = t('retry', 'Retry');
                if (prog) prog.classList.remove('is-on');
                if (bar) bar.style.width = '0%';
                break;
            default:
                label.textContent = queue.length > 1
                    ? t('apply_btn_many', ':n files').replace(':n', queue.length)
                    : t('apply_btn', 'Apply');
                if (prog) prog.classList.remove('is-on');
                if (bar) bar.style.width = '0%';
                if (txt) txt.textContent = '';
                syncEnabled();
        }
    }

    // -- file intake -----------------------------------------------------

    async function readBytes(file) {
        if (typeof file.arrayBuffer === 'function') {
            try { return await file.arrayBuffer(); } catch (e) {}
        }
        return await new Promise(function (res, rej) {
            var r = new FileReader();
            r.onload = function () { res(r.result); };
            r.onerror = function () { rej(r.error || new Error('read failed')); };
            r.readAsArrayBuffer(file);
        });
    }

    async function accept(files) {
        var list = Object.prototype.toString.call(files) === '[object Array]' ? files : [files];
        list = list.filter(function (f) { return f && f.size; });
        if (!list.length) return;
        // Picking again ADDS: «Ещё файлы» would otherwise throw away the queue
        // the person had just built, which is the opposite of what the button
        // says. A file already queued is not added twice.
        var seen = Object.create(null);
        queue.forEach(function (f) { seen[f.name + ':' + f.size] = 1; });
        var fresh = list.filter(function (f) { return !seen[f.name + ':' + f.size]; });
        queue = queue.concat(fresh);
        if (!fresh.length) { renderFiles(); return; }
        funnel('file_picked', { bytes: list[0].size, files: list.length });
        if (queue.length > 1) ae('batch_picked', { n: queue.length });
        await loadActive(queue.length - fresh.length);
    }

    /** Make file `i` the one on screen: its bytes, its document, its preview. */
    async function loadActive(i) {
        var file = queue[i];
        if (!file) return;
        activeIdx = i;
        try {
            var bytes = await readBytes(file);
            source = { file: file, bytes: bytes, name: file.name || 'document.pdf', pageCount: null };
            stage('editing', { tool: SLUG });

            var lib = await loadPdfJs();
            if (pdfDoc) { try { await pdfDoc.destroy(); } catch (e) {} pdfDoc = null; }
            pdfDoc = await lib.getDocument({ data: bytes.slice(0), isEvalSupported: false }).promise;
            source.pageCount = pdfDoc.numPages;

            S.ready = true;
            var out = $('wmName');
            if (out) out.placeholder = baseName(source.name) + '-watermarked.pdf';

            var ws = $('wmWork');
            if (ws) ws.hidden = false;
            var intro = $('wmPitch');
            if (intro) intro.hidden = true;

            if (!preview) {
                preview = window.WmPreview.create({
                    canvas: $('wmCanvas'),
                    getState: previewState,
                    onPlace: onPlace,
                    onCoverage: onCoverage,
                });
            }
            await preview.setDocument(pdfDoc);
            autoFit();
            renderPager();
            renderFiles();
            setExportState('idle');
        } catch (err) {
            var m = String((err && err.message) || err);
            if (/password/i.test(m)) toast(t('err_encrypted', 'Password-protected PDF.'), 'error');
            else toast(t('err_corrupted', 'Could not read the PDF.'), 'error');
            if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'read_failed', msg: m.slice(0, 160) });
            reset();
        }
    }

    function reset() {
        S.ready = false;
        source = null;
        queue = [];
        activeIdx = 0;
        var zone = $('wmUpload');
        if (zone) zone.hidden = false;
        renderFiles();
        delivered = false;
        if (limitTicket) { try { limitTicket.abort(); } catch (e) {} limitTicket = null; }
        if (pdfDoc) { try { pdfDoc.destroy(); } catch (e) {} pdfDoc = null; }
        var ws = $('wmWork'); if (ws) ws.hidden = true;
        var intro = $('wmPitch'); if (intro) intro.hidden = false;
        exportBlob = null;
        setExportState('idle');
    }

    // -- the queue --------------------------------------------------------

    function renderFiles() {
        var host = $('wmFiles');
        if (!host) return;
        host.innerHTML = '';
        host.hidden = queue.length < 1;
        if (queue.length < 1) return;

        // With `multiple` the shared uploader does NOT collapse itself, so the
        // full drop zone would sit above the workspace for ever — on a laptop
        // that is most of the screen this rebuild was fought for. The queue
        // replaces it, and «Ещё файлы» reopens the same picker.
        var zone = $('wmUpload');
        if (zone) zone.hidden = queue.length > 0;

        queue.forEach(function (f, i) {
            var row = document.createElement('div');
            row.className = 'wm-file' + (i === activeIdx ? ' is-on' : '');

            var name = document.createElement('button');
            name.type = 'button';
            name.className = 'wm-file-name';
            name.textContent = f.name;
            name.title = f.name + ' · ' + humanSize(f.size);
            name.addEventListener('click', function () {
                if (i === activeIdx) return;
                loadActive(i);
            });

            var kill = document.createElement('button');
            kill.type = 'button';
            kill.className = 'wm-file-x';
            kill.textContent = '×';
            kill.setAttribute('aria-label', t('file_remove', 'Remove') + ': ' + f.name);
            kill.addEventListener('click', function (ev) {
                ev.stopPropagation();
                queue.splice(i, 1);
                if (!queue.length) { reset(); return; }
                loadActive(Math.min(activeIdx, queue.length - 1));
                ae('file_removed', { left: queue.length });
            });

            row.appendChild(name);
            row.appendChild(kill);
            host.appendChild(row);
        });

        var add = document.createElement('button');
        add.type = 'button';
        add.className = 'wm-file-add';
        add.textContent = t('file_add', '+');
        add.addEventListener('click', function () {
            var up = $('wmUpload');
            if (up && up.__uploaderOpen) up.__uploaderOpen();
        });
        host.appendChild(add);

        // A per-file output name is a control that cannot do anything for a
        // batch, so it goes away rather than sitting there being ignored.
        var nameRow = $('wmNameRow');
        if (nameRow) nameRow.hidden = queue.length > 1;

        var label = $('wmGo') && $('wmGo').querySelector('.wm-go-label');
        if (label && exportState === 'idle') {
            label.textContent = queue.length > 1
                ? t('apply_btn_many', ':n files').replace(':n', queue.length)
                : t('apply_btn', 'Apply');
        }
    }

    // -- preview callbacks ------------------------------------------------

    function onPlace(fx, fy, snapped) {
        if (snapped) {
            S.position = snapped;
        } else {
            S.position = 'free';
            S.freeX = fx;
            S.freeY = fy;
        }
        markGrid();
        touched();
        ae('drag_place', { snapped: snapped ? 1 : 0 });
    }

    function onCoverage(ratio) {
        var note = $('wmUnderWarn');
        if (!note) return;
        // A page that paints its own opaque background hides anything beneath
        // it. Saying so before the click beats explaining it afterwards.
        note.hidden = !(S.layer === 'under' && ratio > 0.985);
    }

    function renderPager() {
        var wrap = $('wmPager');
        if (!wrap || !preview) return;
        var n = preview.pages();
        wrap.hidden = n <= 1;
        var lbl = $('wmPageLabel');
        if (lbl) {
            lbl.textContent = (preview.page() + 1) + ' / ' + n;
        }
        var meta = $('wmPageMeta');
        if (meta && source) meta.textContent = pluralPages(source.pageCount || n);
        var prev = $('wmPrev'), next = $('wmNext');
        if (prev) prev.disabled = preview.page() <= 0;
        if (next) next.disabled = preview.page() >= n - 1;
    }

    async function goPage(delta) {
        if (!preview) return;
        await preview.setPage(preview.page() + delta);
        renderPager();
        onCoverage(preview.coverage());
    }

    // -- apply -----------------------------------------------------------

    function buildWorker() {
        if (worker) return worker;
        worker = new Worker(CFG.workerUrl);
        worker.addEventListener('message', onMsg);
        worker.addEventListener('error', function (e) {
            if (window.__aeCritical) {
                window.__aeCritical('tool_error', { slug: SLUG, klass: 'worker', msg: String(e.message || e).slice(0, 400) });
            }
            // Reject whatever stampOne is awaiting: a worker that dies mid-batch
            // otherwise leaves the loop waiting on a promise nothing will settle,
            // and the button sits on "Cancel" for ever.
            var who = pending; pending = null;
            if (who) {
                var err = new Error(String(e.message || e));
                err.klass = 'worker';
                who.reject(err);
                return;
            }
            toast(t('err_fatal', 'Could not apply the watermark.'), 'error');
            releaseTicket();
            setExportState('err');
        });
        return worker;
    }

    function releaseTicket() {
        if (limitTicket) { try { limitTicket.abort(); } catch (e) {} limitTicket = null; }
    }

    /**
     * Stamp ONE file through the worker and resolve with its Blob.
     *
     * The worker takes one job per message and answers with one `done`, so a
     * batch is this awaited in a loop rather than a second code path: the
     * single-file case and the tenth file of a batch go through exactly the
     * same stamping, and there is no second implementation to drift.
     */
    function stampOne(file, idx, total) {
        return new Promise(function (resolve, reject) {
            pending = { resolve: resolve, reject: reject, idx: idx, total: total };
            readBytes(file).then(function (bytes) {
                var msg = {
                    type: 'apply',
                    file: { bytes: bytes, name: file.name },
                    mode: S.mode,
                    layout: {
                        position: S.position,
                        freeX: S.freeX, freeY: S.freeY,
                        opacity: S.opacity,
                        rotation: S.rotation,
                        tile: S.tile,
                        tileDensity: S.tileDensity,
                        layer: S.layer,
                        pageRange: S.pageRange,
                    },
                    opts: {
                        outputName: total > 1
                            ? (baseName(file.name) + '-watermarked.pdf')
                            : ((S.outputName || '').trim() || (baseName(file.name) + '-watermarked.pdf')),
                    },
                };
                var transfers = [msg.file.bytes];
                if (S.mode === 'text') {
                    msg.text = { text: S.text.trim(), font: S.font, size: S.size, color: S.color };
                    buildWorker().postMessage(msg, transfers);
                    return;
                }
                return S.imageFile.arrayBuffer().then(function (buf) {
                    msg.image = {
                        bytes: new Uint8Array(buf),
                        format: /png$/i.test(S.imageFile.type) ? 'png' : 'jpg',
                        widthPct: S.imageWidthPct,
                    };
                    transfers.push(msg.image.bytes.buffer);
                    buildWorker().postMessage(msg, transfers);
                });
            }).catch(reject);
        });
    }

    async function apply() {
        if (exportState === 'busy') {
            if (worker) worker.postMessage({ type: 'cancel' });
            return;
        }
        if (exportState === 'done' && exportBlob) {
            window.__downloadFile(exportBlob, exportName);
            ae('redownload', {});
            return;
        }
        if (!queue.length || !source) { toast(t('err_no_file', 'Choose a PDF first.'), 'error'); return; }
        if (S.mode === 'text' && !(S.text || '').trim()) {
            toast(t('err_no_text', 'Enter the watermark text.'), 'error');
            var ti = $('wmText'); if (ti) ti.focus();
            return;
        }
        if (S.mode === 'image' && !S.imageFile) {
            toast(t('err_no_image', 'Choose an image first.'), 'error');
            return;
        }

        // begin() is synchronous, so the click's activation is still ours when
        // openBatch asks for a folder below. Order matters: the cap has to
        // refuse BEFORE a folder picker opens, or the person chooses a folder
        // and is then told they have no runs left.
        if (window.__toolLimit && !limitTicket) {
            limitTicket = window.__toolLimit.begin(SLUG, queue.map(function (f) {
                return f.name + ':' + f.size;
            }));
            if (!limitTicket) return;           // the shared note is already up
        }

        var many = queue.length > 1;
        var batch = null;
        if (many) {
            // FIRST await after the cap: showDirectoryPicker lives on the
            // click's activation, and anything slower awaited before it spends
            // that activation and silently demotes the folder lane to a ZIP.
            var bd = window.__batchDeliver;
            if (!bd && window.__ensureBatchDeliver) {
                try { bd = await window.__ensureBatchDeliver(); } catch (e) { bd = null; }
            }
            if (!bd) {
                // No looping downloads as a fallback. The browser hands over ONE
                // file per gesture, throws the rest away silently, and the tab
                // then stops downloading anything at all — across the whole
                // site, not just here. A batch we cannot deliver properly is a
                // batch we refuse, out loud.
                toast(t('err_fatal', 'Could not apply the watermark.'), 'error');
                if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'batch_primitive_missing' });
                releaseTicket();
                setExportState('err');
                return;
            }
            batch = await bd.openBatch({
                slug: SLUG,
                zipName: 'watermarked.zip',
                total: queue.length,
            });
        }

        substitutionTold = false;
        setExportState('busy');
        stage('processing', { tool: SLUG });
        var bar = $('wmProgBar'), txt = $('wmProgText');
        if (bar) bar.style.width = '0%';
        if (txt) txt.textContent = t('stage_stamping', 'Stamping');

        try {
            if (!many) {
                var one = await stampOne(queue[0] || source.file, 0, 1);
                exportBlob = one.blob;
                exportName = one.name;
                // ⚠️ The verdict is the RESOLVED VALUE — __downloadFile refuses
                // by RETURNING false and practically never throws, so calling it
                // and then setting `delivered` is an assumption, not a fact.
                var okOne = await window.__downloadFile(exportBlob, exportName);
                if (okOne === false) { releaseTicket(); setExportState('err'); return; }
                delivered = true;
                finishRun({ files: 1, via: 'single' });
                return;
            }

            var written = 0;
            for (var i = 0; i < queue.length; i++) {
                var res = await stampOne(queue[i], i, queue.length);
                // Only the batch lane writes here. There is deliberately no
                // `else __downloadFile(...)`: see above — one file per gesture.
                await batch.write(res.name, res.blob);
                written++;
            }
            var out = await batch.finish();
            if (out && out.via === 'refused') {
                releaseTicket();
                setExportState('err');
                return;
            }
            delivered = true;
            finishRun({ files: written, via: (out && out.via) || 'none' });
        } catch (err) {
            if (err && err.klass === 'cancel') {
                if (batch && batch.abort) batch.abort();
                releaseTicket();
                setExportState('idle');
                if (txt) txt.textContent = '';
                return;
            }
            if (batch && batch.abort) batch.abort();
            reportError(err);
        }
    }

    /** Everything that has to happen once a run has actually been handed over. */
    function finishRun(info) {
        setExportState('done');
        stage('complete', { tool: SLUG });
        if (limitTicket) { try { limitTicket.commit(); } catch (e) {} limitTicket = null; }
        funnel('download', {
            mode: S.mode, layer: S.layer, tile: S.tile ? 1 : 0,
            pos: S.position, rot: S.rotation, pages: source ? source.pageCount : 0,
            files: info.files, via: info.via,
        });
        if (window.__aeToolComplete) {
            window.__aeToolComplete({ mode: S.mode, layer: S.layer, files: info.files });
        }
    }

    function reportError(err) {
        var code = String((err && (err.msg || err.message)) || err || '').split(':')[0];
        var map = {
            ENCRYPTED: t('err_encrypted', ''), CORRUPTED: t('err_corrupted', ''),
            NO_TEXT: t('err_no_text', ''), NO_IMAGE: t('err_no_image', ''),
            BAD_IMAGE: t('err_bad_image', ''), UNSUPPORTED_SCRIPT: t('err_unsupported_script', ''),
        };
        toast(map[code] || t('err_fatal', 'Failed.'), 'error');
        releaseTicket();
        setExportState('err');
    }

    /**
     * The worker answers one job at a time, so this only has to hand each
     * answer to whoever is waiting for it — `pending`, set by stampOne. Doing
     * the delivery here, as it did before the batch lane, meant the second file
     * of a batch would download itself in the middle of the loop.
     */
    function onMsg(ev) {
        var m = ev.data || {};
        var bar = $('wmProgBar'), txt = $('wmProgText');

        if (m.type === 'progress') {
            var phase = m.stage === 'saving' ? t('stage_saving', 'Saving')
                : (m.stage === 'loading' ? t('stage_loading', 'Loading') : t('stage_stamping', 'Stamping'));
            var pages = m.total > 1 ? ' · ' + (m.idx + 1) + '/' + m.total : '';
            var files = (pending && pending.total > 1)
                ? t('stage_file', 'File :i of :n')
                    .replace(':i', pending.idx + 1).replace(':n', pending.total) + ' · '
                : '';
            if (txt) txt.textContent = files + phase + pages;
            // Across a batch the bar tracks the WHOLE job, not the page inside
            // the current file: a bar that resets to zero nine times reads as
            // nine failures.
            if (bar) {
                var pct = pending && pending.total > 1
                    ? ((pending.idx + (m.pct || 0) / 100) / pending.total) * 100
                    : m.pct;
                bar.style.width = Math.max(0, Math.min(100, pct)) + '%';
            }
            return;
        }

        if (m.type === 'done') {
            var res = { blob: new Blob([m.bytes], { type: 'application/pdf' }), name: m.name };
            var who = pending; pending = null;
            if (who) who.resolve(res);
            return;
        }

        if (m.type === 'font_substituted') {
            // Saying it out loud: silently changing the typeface of a watermark
            // the visitor chose is their decision, not ours. Once per run —
            // ten files would otherwise raise ten identical toasts.
            if (!substitutionTold) {
                substitutionTold = true;
                toast((t('font_substituted', '') || '').replace(':font', m.used || ''), 'info');
            }
            return;
        }

        if (m.type === 'error') {
            var w2 = pending; pending = null;
            var err = new Error(m.msg || 'failed');
            err.klass = m.klass;
            err.msg = m.msg;
            if (w2) w2.reject(err);
            else if (m.klass !== 'cancel') reportError(err);
        }
    }

    // -- wiring ----------------------------------------------------------

    function bindRange(id, valId, key, suffix, after) {
        var el = $(id), out = $(valId);
        if (!el) return;
        el.addEventListener('input', function () {
            S[key] = parseInt(el.value, 10);
            if (out) out.textContent = el.value + (suffix || '');
            if (after) after();
            touched();
        });
        if (out) out.textContent = el.value + (suffix || '');
    }

    function segment(selector, apply2) {
        var btns = document.querySelectorAll(selector);
        for (var i = 0; i < btns.length; i++) {
            (function (b) {
                b.addEventListener('click', function () {
                    for (var j = 0; j < btns.length; j++) {
                        var on = btns[j] === b;
                        btns[j].classList.toggle('is-on', on);
                        btns[j].setAttribute('aria-pressed', on ? 'true' : 'false');
                    }
                    apply2(b.dataset.v, b);
                });
            })(btns[i]);
        }
    }

    function init() {
        var up = $('wmUpload');
        if (up) {
            var onFile = function (file, detail) {
                var d = detail || {};
                var list = (d.files && d.files.length) ? Array.prototype.slice.call(d.files)
                    : (file ? [file] : []);
                if (list.length) accept(list);
            };
            if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(up, onFile);
            else {
                up.addEventListener('tool-uploader:file', function (ev) {
                    var d = (ev && ev.detail) || {};
                    onFile(d.file || null, d);
                });
            }
            up.addEventListener('tool-uploader:clear', reset);
        }

        // mode
        segment('.wm-mode', function (v) {
            S.mode = v;
            var tp = $('wmTextPane'), ip = $('wmImagePane');
            if (tp) tp.hidden = v !== 'text';
            if (ip) ip.hidden = v !== 'image';
            ae('mode', { v: v });
            touched();
        });

        // text + presets
        var ti = $('wmText');
        if (ti) {
            ti.addEventListener('input', function () { S.text = ti.value; autoFit(); touched(); });
        }
        var chips = document.querySelectorAll('.wm-preset');
        for (var i = 0; i < chips.length; i++) {
            (function (c) {
                c.addEventListener('click', function () {
                    S.text = c.dataset.v || c.textContent.trim();
                    if (ti) ti.value = S.text;
                    autoFit();
                    ae('preset', { v: S.text.slice(0, 24) });
                    touched();
                });
            })(chips[i]);
        }

        var fo = $('wmFont');
        if (fo) fo.addEventListener('change', function () { S.font = fo.value; touched(); });
        var sz = $('wmSize');
        if (sz) sz.addEventListener('input', function () {
            var v = parseInt(sz.value, 10);
            if (!isNaN(v)) { S.size = Math.max(6, Math.min(400, v)); S.sizeTouched = true; touched(); }
        });
        var fit = $('wmFit');
        if (fit) fit.addEventListener('click', function () {
            var v = fitSize();
            if (!v) return;
            S.size = v;
            if (sz) sz.value = v;
            ae('fit_width', { size: v });
            touched();
        });
        var co = $('wmColor');
        if (co) co.addEventListener('input', function () { S.color = co.value; touched(); });

        // image
        var pick = $('wmImgPick'), input = $('wmImgInput'), info = $('wmImgInfo'), clear = $('wmImgClear');
        if (pick && input) pick.addEventListener('click', function () { input.click(); });
        if (clear) clear.addEventListener('click', function () {
            S.imageFile = null; S.image = null;
            if (info) info.textContent = '—';
            clear.hidden = true;
            if (input) input.value = '';
            touched();
        });
        if (input) input.addEventListener('change', async function () {
            var f = input.files && input.files[0];
            if (!f) return;
            if (!/image\/(jpeg|png)/i.test(f.type || '')) {
                toast(t('err_bad_image', 'Use a JPG or PNG.'), 'error');
                input.value = '';
                return;
            }
            S.imageFile = f;
            try {
                S.image = await createImageBitmap(f);
            } catch (e) {
                S.image = await new Promise(function (res, rej) {
                    var im = new Image();
                    im.onload = function () { res(im); };
                    im.onerror = rej;
                    im.src = URL.createObjectURL(f);
                });
            }
            if (info) info.textContent = f.name + ' · ' + humanSize(f.size);
            if (clear) clear.hidden = false;
            touched();
        });

        // placement
        var cells = document.querySelectorAll('.wm-cell');
        for (var c2 = 0; c2 < cells.length; c2++) {
            (function (b) {
                b.addEventListener('click', function () {
                    if (S.tile) return;
                    setPosition(b.dataset.pos);
                });
            })(cells[c2]);
        }

        var tileBtn = $('wmTile');
        if (tileBtn) tileBtn.addEventListener('click', function () {
            S.tile = !S.tile;
            tileBtn.classList.toggle('is-on', S.tile);
            tileBtn.setAttribute('aria-pressed', S.tile ? 'true' : 'false');
            var d = $('wmDensityRow');
            if (d) d.hidden = !S.tile;
            markGrid();
            ae('tile', { on: S.tile ? 1 : 0 });
            touched();
        });

        bindRange('wmImgWidth', 'wmImgWidthVal', 'imageWidthPct', '%', null);
        bindRange('wmDensity', 'wmDensityVal', 'tileDensity', '', null);
        bindRange('wmOpacity', 'wmOpacityVal', 'opacity', '%', null);
        bindRange('wmRotation', 'wmRotationVal', 'rotation', '°', autoFit);

        var diag = $('wmDiagonal');
        if (diag) diag.addEventListener('click', function () {
            S.rotation = S.rotation === -45 ? 0 : -45;
            var r = $('wmRotation'), rv = $('wmRotationVal');
            if (r) r.value = S.rotation;
            if (rv) rv.textContent = S.rotation + '°';
            diag.classList.toggle('is-on', S.rotation === -45);
            autoFit();
            ae('diagonal', { rot: S.rotation });
            touched();
        });

        segment('.wm-layer', function (v) {
            S.layer = v;
            if (preview) onCoverage(preview.coverage());
            ae('layer', { v: v });
            touched();
        });

        // pages + name
        var pr = $('wmRange');
        if (pr) pr.addEventListener('input', function () { S.pageRange = pr.value; touched(); });
        var rchips = document.querySelectorAll('.wm-rchip');
        for (var r2 = 0; r2 < rchips.length; r2++) {
            (function (b) {
                b.addEventListener('click', function () {
                    S.pageRange = b.dataset.v || '';
                    if (pr) pr.value = S.pageRange;
                    touched();
                });
            })(rchips[r2]);
        }
        var nm = $('wmName');
        if (nm) nm.addEventListener('input', function () { S.outputName = nm.value; touched(); });

        // pager
        var prev = $('wmPrev'), next = $('wmNext');
        if (prev) prev.addEventListener('click', function () { goPage(-1); });
        if (next) next.addEventListener('click', function () { goPage(1); });

        var go = $('wmGo');
        if (go) go.addEventListener('click', apply);

        // remembered settings: what the person picked last time is almost always
        // what they want again, and none of it is file-specific.
        if (window.__toolSettings) {
            var TS = window.__toolSettings;
            TS.bind(SLUG, $('wmFont'), 'font', 'helvetica-bold');
            TS.bind(SLUG, $('wmColor'), 'color', '#999999');
            TS.bind(SLUG, $('wmOpacity'), 'opacity', '30');
            S.font = ($('wmFont') || {}).value || S.font;
            S.color = ($('wmColor') || {}).value || S.color;
            S.opacity = parseInt(($('wmOpacity') || {}).value, 10) || S.opacity;
            var ov = $('wmOpacityVal');
            if (ov) ov.textContent = S.opacity + '%';
        }

        window.addEventListener('pagehide', function () {
            if (source && !delivered) ae('left_without_download', { pages: source.pageCount || 0 });
        });

        markGrid();
        setExportState('idle');

        // Read-only introspection for the browser tests.
        window.__wmDebug = {
            get state() { return JSON.parse(JSON.stringify(previewState(), function (k, v) { return k === 'image' ? undefined : v; })); },
            get pages() { return preview ? preview.pages() : 0; },
            get page() { return preview ? preview.page() : 0; },
            get coverage() { return preview ? preview.coverage() : 0; },
            get files() { return queue.map(function (f) { return f.name; }); },
            get activeFile() { return activeIdx; },
            places: function () {
                return preview ? preview.placements().spots : [];
            },
            box: function () {
                return preview ? preview.placements().box : null;
            },
        };
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
})();
